// src/components/upload/PhotoInput.jsx
import { useRef, useState, useCallback, useEffect } from "react";
import Button from "./Button";
import Avatar from "./Avatar";

function CameraIcon({ className = "w-6 h-6" }) {
    return (
        <svg
            viewBox="0 0 24 24"
            className={className}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
        >
            <path
                d="M3 7h3l2-3h8l2 3h3v12H3z"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <circle cx="12" cy="13" r="4" />
        </svg>
    );
}

function PencilIcon({ className = "w-4 h-4" }) {
    return (
        <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

export default function PhotoInput({
    value,                 // preview URL (string)
    onChange,              // ({ file, previewUrl }) => void
    label = "Pet photo",
    helpText = "Tap to add photo",
    className = "",
}) {
    const inputRef = useRef(null);
    const [preview, setPreview] = useState(value || "");
    const [dragOver, setDragOver] = useState(false);

    useEffect(() => {
        setPreview(value || "");
    }, [value]);

    const openPicker = () => inputRef.current?.click();

    const handleFile = useCallback(
        (file) => {
            if (!file) return;
            const url = URL.createObjectURL(file);
            setPreview(url);
            onChange?.({ file, previewUrl: url });
        },
        [onChange]
    );

    const onInputChange = (e) => handleFile(e.target.files?.[0]);

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFile(e.dataTransfer?.files?.[0]);
    };

    return (
        <div className={`flex flex-col items-center text-center ${className.includes('w-full') ? 'w-full' : ''}`}>
            <div
                role="button"
                tabIndex={0}
                onClick={openPicker}
                onKeyDown={(e) =>
                    (e.key === "Enter" || e.key === " ") && openPicker()
                }
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={[
                    "relative grid place-items-center rounded",
                    className.includes("h-") ? "" : "h-32 sm:h-36 md:h-40",
                    className.includes("w-") ? "" : "w-32 sm:w-36 md:w-40",
                    // Default border logic
                    "border transition-all cursor-pointer select-none overflow-hidden group",
                    dragOver
                        ? "border-primary bg-gray-100"
                        : "border-gray-100 bg-gray-50 hover:bg-gray-100",
                    preview ? "bg-white border-transparent" : "",
                    className
                ].join(" ")}
                aria-label={label}
            >
                {preview ? (
                    <>
                        <img
                            src={preview}
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover transition-transform group-hover:scale-105"
                        />
                    </>
                ) : (
                    <div className="flex flex-col items-center gap-3 px-4">
                        <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 text-gray-400 transition-colors group-hover:bg-primary group-hover:text-white">
                            <CameraIcon className="w-6 h-6" />
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <span className="text-sm font-bold text-gray-900 tracking-tight transition-colors group-hover:text-primary">
                                {label}
                            </span>
                            {helpText && (
                                <span className="text-[11px] font-medium text-gray-400 leading-tight">
                                    {helpText}
                                </span>
                             )}
                        </div>
                    </div>
                )}

                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    onChange={onInputChange}
                    className="hidden"
                />
            </div>

            {!preview && (
                <div className="sr-only" aria-hidden>
                    <Avatar name="" />
                </div>
            )}
        </div>
    );
}